import React, { useState } from 'react'
import { api } from '../api'
import { calculateSemiFinishedTotalCost, isSemiFinishedEligibleForRecipeReport, formatQuantity } from '../costUtils'
import {
  Category,
  ComponentItem,
  ComponentType,
  Packaging,
  Product,
  RawMaterial,
  SemiFinishedProduct,
  TradingGood,
} from '../types'
import { SearchableComponentSelect, SelectableComponentOption } from './SearchableComponentSelect'

interface Props {
  semiFinished: SemiFinishedProduct[]
  setSemiFinished?: React.Dispatch<React.SetStateAction<SemiFinishedProduct[]>>
  setDemoSemiFinished?: React.Dispatch<React.SetStateAction<any[]>>
  products: Product[]
  rawMaterials: RawMaterial[]
  packagings: Packaging[]
  tradingGoods?: TradingGood[]
  categories: Category[]
  onRefresh: () => Promise<void> | void
  onRefreshSemiFinished?: () => Promise<void> | void
  onCascadeRefresh?: () => Promise<void> | void
  onOpenReport?: (item: SemiFinishedProduct, type: 'semi_finished') => void
}

function PieChartIcon({ size = 13, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function PencilIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function SettingsGearIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

export function SemiFinishedView({
  semiFinished,
  setSemiFinished,
  setDemoSemiFinished,
  products,
  rawMaterials,
  packagings,
  tradingGoods = [],
  categories,
  onRefresh,
  onRefreshSemiFinished: _onRefreshSemiFinished,
  onCascadeRefresh,
  onOpenReport,
}: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingItem, setEditingItem] = useState<SemiFinishedProduct | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [activeItemForAddComponent, setActiveItemForAddComponent] = useState<SemiFinishedProduct | null>(null)
  const [editingCompQuantity, setEditingCompQuantity] = useState<{
    parentItem: SemiFinishedProduct
    component: ComponentItem
    quantity: string
  } | null>(null)
  const [gearMenu, setGearMenu] = useState<{ item: SemiFinishedProduct; x: number; y: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const [localHiddenItemIds, setLocalHiddenItemIds] = useState<Set<string>>(new Set())
  const [localHiddenComponentIds, setLocalHiddenComponentIds] = useState<Set<string>>(new Set())

  // Search and Category Filter
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const filteredSemiFinished = React.useMemo(() => {
    return semiFinished.filter((item) => {
      if (localHiddenItemIds.has(item.id)) return false
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.code && item.code.toLowerCase().includes(term))
      const matchCategory = !selectedCategory || item.category_id === selectedCategory
      return matchSearch && matchCategory
    })
  }, [semiFinished, searchTerm, selectedCategory, localHiddenItemIds])

  // Maps for recursive BOM tree lookup
  const semiFinishedMap = React.useMemo(() => {
    return new Map(semiFinished.map((s) => [s.id, s]))
  }, [semiFinished])

  const productsMap = React.useMemo(() => {
    return new Map(products.map((p) => [p.id, p]))
  }, [products])

  const rawMaterialsMap = React.useMemo(() => {
    return new Map(rawMaterials.map((r) => [r.id, r]))
  }, [rawMaterials])

  const packagingsMap = React.useMemo(() => {
    return new Map(packagings.map((p) => [p.id, p]))
  }, [packagings])

  const tradingGoodsMap = React.useMemo(() => {
    return new Map((tradingGoods || []).map((t) => [t.id, t]))
  }, [tradingGoods])

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // Add Form
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [unit, setUnit] = useState('Adet')
  const [recipeOutputQuantity, setRecipeOutputQuantity] = useState('1')
  const [image, setImage] = useState('')
  const [barcode, setBarcode] = useState('')
  const [description, setDescription] = useState('')

  // Edit Form
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [editUnit, setEditUnit] = useState('Adet')
  const [editRecipeOutputQuantity, setEditRecipeOutputQuantity] = useState('1')
  const [editImage, setEditImage] = useState('')
  const [editBarcode, setEditBarcode] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // Component Modal Form: 2-step flow
  const [selectedCompType, setSelectedCompType] = useState<ComponentType>('raw_material')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [compQuantity, setCompQuantity] = useState('1.0000')
  const [compUnitMode, setCompUnitMode] = useState<'kg' | 'g'>('kg')
  const [compError, setCompError] = useState<string | null>(null)

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const created = await api.createSemiFinished({
        code,
        name,
        category_id: categoryId || null,
        unit,
        maliyet: 0.0,
        recipe_output_quantity: parseFloat(recipeOutputQuantity) || 1.0,
        image: image || null,
        barcode: barcode || null,
        description: description || null,
      })
      if (setSemiFinished) {
        setSemiFinished((prev) => [created, ...prev])
      } else {
        await onRefresh()
      }
      setShowAddModal(false)
      setCode('')
      setName('')
      setUnit('Adet')
      setRecipeOutputQuantity('1')
      setImage('')
      setBarcode('')
      setDescription('')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openEditModal = (item: SemiFinishedProduct) => {
    setEditingItem(item)
    setEditCode(item.code)
    setEditName(item.name)
    setEditCategoryId(item.category_id || '')
    setEditUnit(item.unit || 'Adet')
    setEditRecipeOutputQuantity(String(item.recipe_output_quantity || '1'))
    setEditImage(item.image || '')
    setEditBarcode(item.barcode || '')
    setEditDescription(item.description || '')
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem) return
    setError(null)
    try {
      const updated = await api.updateSemiFinished(editingItem.id, {
        code: editCode,
        name: editName,
        category_id: editCategoryId || null,
        unit: editUnit,
        maliyet: 0.0,
        recipe_output_quantity: parseFloat(editRecipeOutputQuantity) || 1.0,
        image: editImage || null,
        barcode: editBarcode || null,
        description: editDescription || null,
      })
      setEditingItem(null)
      if (setSemiFinished) {
        setSemiFinished((prev) =>
          prev.map((s) => (s.id === updated.id ? { ...s, ...updated, components: s.components } : s))
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Bu yarı mamulü silmek istediğinize emin misiniz?')) return
    const itemToDelete = semiFinished.find((s) => s.id === id)
    const originalIndex = semiFinished.findIndex((s) => s.id === id)

    if (setSemiFinished) {
      setSemiFinished((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenItemIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteSemiFinished(id)
    } catch (err: any) {
      if (setSemiFinished && itemToDelete) {
        setSemiFinished((prev) => {
          if (prev.some((s) => s.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenItemIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenItemIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  // --- Component Management (Cascading Tree & Root-Only Addition) ---
  const openAddComponentModal = (item: SemiFinishedProduct) => {
    setActiveItemForAddComponent(item)
    setSelectedCompType('raw_material')
    setSelectedItemId('')
    setCompQuantity('1.0000')
    setCompUnitMode('kg')
    setCompError(null)
  }

  const getSelectedComponentUnit = (): string => {
    if (!selectedItemId) return ''
    if (selectedCompType === 'raw_material') {
      return rawMaterials.find((r) => r.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'packaging') {
      return packagings.find((p) => p.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished.find((s) => s.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'product') {
      return products.find((p) => p.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).find((t) => t.id === selectedItemId)?.unit || ''
    }
    return ''
  }

  const handleAddComponent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeItemForAddComponent || !selectedItemId) return
    setCompError(null)
    try {
      const selectedUnit = getSelectedComponentUnit()
      const isKg = selectedUnit.trim().toLowerCase() === 'kg'
      const cleanQty = compQuantity.replace(',', '.')
      let numVal = parseFloat(cleanQty) || 1.0
      if (isKg && compUnitMode === 'g') {
        numVal = numVal / 1000.0
      }

      const parentN = Number(activeItemForAddComponent.recipe_output_quantity || 1)
      const mVal = numVal
      const unitAmt = parentN > 0 ? mVal / parentN : mVal

      const newComp = await api.addSemiFinishedComponent(activeItemForAddComponent.id, {
        component_type: selectedCompType,
        component_id: selectedItemId,
        recipe_amount: mVal,
        unit_amount: unitAmt,
        quantity: unitAmt,
      })
      const targetId = activeItemForAddComponent.id
      setSelectedItemId('')
      setCompQuantity('1.0000')
      setCompUnitMode('kg')
      setActiveItemForAddComponent(null)
      // Keep root node open
      setExpandedKeys((prev) => new Set(prev).add(`sf_${targetId}`))
      if (setSemiFinished) {
        setSemiFinished((prev) =>
          prev.map((s) => {
            if (s.id !== targetId) return s
            return {
              ...s,
              components: [...(s.components || []), newComp],
            }
          })
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setCompError(err.message)
    }
  }

  const handleDeleteComponent = async (semiFinishedId: string, compId: string) => {
    if (!confirm('Bu bileşeni yarı mamulden çıkarmak istediğinizden emin misiniz?')) return
    const parentItem = semiFinished.find((s) => s.id === semiFinishedId)
    const compToDelete = parentItem?.components?.find((c) => c.id === compId)
    const originalIndex = parentItem?.components?.findIndex((c) => c.id === compId) ?? -1

    if (setSemiFinished) {
      setSemiFinished((prev) =>
        prev.map((s) => {
          if (s.id !== semiFinishedId) return s
          return {
            ...s,
            components: (s.components || []).filter((c) => c.id !== compId),
          }
        })
      )
    }
    setLocalHiddenComponentIds((prev) => new Set(prev).add(compId))

    try {
      await api.deleteSemiFinishedComponent(semiFinishedId, compId)
    } catch (err: any) {
      if (setSemiFinished && compToDelete && originalIndex >= 0) {
        setSemiFinished((prev) =>
          prev.map((s) => {
            if (s.id !== semiFinishedId) return s
            const comps = [...(s.components || [])]
            if (!comps.some((c) => c.id === compId)) {
              comps.splice(originalIndex, 0, compToDelete)
            }
            return { ...s, components: comps }
          })
        )
      }
      setLocalHiddenComponentIds((prev) => {
        const next = new Set(prev)
        next.delete(compId)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenComponentIds((prev) => {
      const next = new Set(prev)
      next.delete(compId)
      return next
    })
  }

  const handleSaveComponentQuantity = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCompQuantity) return
    const parentN = Number(editingCompQuantity.parentItem.recipe_output_quantity || 1)
    const mVal = parseFloat(editingCompQuantity.quantity) || 1.0
    const unitAmt = parentN > 0 ? mVal / parentN : mVal
    try {
      const updatedComp = await api.updateSemiFinishedComponent(
        editingCompQuantity.parentItem.id,
        editingCompQuantity.component.id,
        {
          recipe_amount: mVal,
          unit_amount: unitAmt,
          quantity: unitAmt,
        }
      )
      const targetParentId = editingCompQuantity.parentItem.id
      const targetCompId = editingCompQuantity.component.id
      setEditingCompQuantity(null)
      if (setSemiFinished) {
        setSemiFinished((prev) =>
          prev.map((s) => {
            if (s.id !== targetParentId) return s
            return {
              ...s,
              components: (s.components || []).map((c) =>
                c.id === targetCompId
                  ? {
                      ...c,
                      ...updatedComp,
                      recipe_amount: mVal,
                      unit_amount: unitAmt,
                      quantity: unitAmt,
                    }
                  : c
              ),
            }
          })
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      alert(err.message)
    }
  }

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleExportExcel = async () => {
    setIsProcessing(true)
    try {
      await api.exportSemiFinishedExcel()
    } catch (err: any) {
      alert(`Excel indirme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const confirmed = window.confirm(
      'Excel dosyasındaki yarı mamuller ve reçete ağaçları sisteme aktarılacaktır.\n\n' +
      '• Eşleşen yarı mamuller ve reçete bileşenleri güncellenecek, yeni yarı mamuller eklenecektir.\n' +
      '• Excel dosyasında yer almayan mevcut kayıtlar silinmeyecektir (korunacaktır).\n\n' +
      'Devam etmek istiyor musunuz?'
    )
    if (!confirmed) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      const res = await api.importSemiFinishedExcel(file)
      alert(`Excel başarıyla işlendi:\n${res.created} yeni eklendi, ${res.updated} güncellendi.`)
      if (onCascadeRefresh) {
        await onCascadeRefresh()
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsProcessing(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Available items for selected component type
  const getSelectableItems = (): SelectableComponentOption[] => {
    if (selectedCompType === 'raw_material') {
      return rawMaterials.map((r) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        unit: r.unit,
        maliyet: Number(r.maliyet || 0),
        typeLabel: 'Hammadde',
        label: `[Hammadde] ${r.code} — ${r.name} (₺${Number(r.maliyet).toFixed(2)} / ${r.unit})`,
      }))
    } else if (selectedCompType === 'packaging') {
      return packagings.map((p) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        unit: p.unit,
        maliyet: Number(p.maliyet || 0),
        typeLabel: 'Ambalaj',
        label: `[Ambalaj] ${p.code} — ${p.name} (₺${Number(p.maliyet).toFixed(2)} / ${p.unit})`,
      }))
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished
        .filter((s) => s.id !== activeItemForAddComponent?.id)
        .map((s) => ({
          id: s.id,
          code: s.code,
          name: s.name,
          unit: s.unit,
          maliyet: Number(s.maliyet || 0),
          typeLabel: 'Yarı Mamul',
          label: `[Yarı Mamul] ${s.code} — ${s.name} (₺${Number(s.maliyet).toFixed(2)} / ${s.unit})`,
        }))
    } else if (selectedCompType === 'product') {
      return products.map((p) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        unit: p.unit,
        maliyet: Number(p.maliyet || 0),
        typeLabel: 'Mamul Ürün',
        label: `[Mamul Ürün] ${p.code} — ${p.name} (₺${Number(p.maliyet).toFixed(2)} / ${p.unit})`,
      }))
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).map((t) => ({
        id: t.id,
        code: t.code,
        name: t.name,
        unit: t.unit || 'Adet',
        maliyet: Number(t.maliyet || 0),
        typeLabel: 'Al-Sat Ürün',
        label: `[Al-Sat Ürün] ${t.code} — ${t.name} (₺${Number(t.maliyet).toFixed(2)} / ${t.unit || 'Adet'})`,
      }))
    }
    return []
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'raw_material':
        return <span className="badge badge-info">Hammadde</span>
      case 'packaging':
        return <span className="badge badge-warning">Ambalaj</span>
      case 'semi_finished':
        return <span className="badge badge-primary">Yarı Mamul</span>
      case 'product':
        return <span className="badge badge-success">Mamul Ürün</span>
      case 'trading_good':
        return <span className="badge badge-purple" style={{ backgroundColor: '#6f42c1', color: '#fff' }}>Al-Sat</span>
      default:
        return <span className="badge badge-secondary">{type}</span>
    }
  }

  // Recursively compute the unit cost of a component including all its sub-components
  const getRecursiveUnitCost = React.useCallback(
    (
      componentType: ComponentType,
      componentId: string,
      baseCost: number,
      visited: Set<string> = new Set()
    ): number => {
      if (visited.has(componentId)) {
        return baseCost
      }

      if (componentType === 'raw_material' || componentType === 'packaging' || componentType === 'trading_good') {
        return baseCost
      }

      const nextVisited = new Set(visited)
      nextVisited.add(componentId)

      let subCost = 0
      if (componentType === 'semi_finished') {
        const semi = semiFinishedMap.get(componentId)
        const directBase = Number(baseCost || semi?.maliyet || 0)
        const subComps = semi?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      if (componentType === 'product') {
        const prod = productsMap.get(componentId)
        const directBase = Number(baseCost || prod?.maliyet || 0)
        const subComps = prod?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      return baseCost
    },
    [semiFinishedMap, productsMap]
  )

  const renderComponentTree = (
    rootItem: SemiFinishedProduct,
    components: ComponentItem[],
    depth: number,
    parentPath: string,
    ancestorIds: Set<string>
  ): React.ReactNode[] => {
    return components
      .filter((comp) => !localHiddenComponentIds.has(comp.id))
      .map((comp) => {
      const childPath = `${parentPath}/${comp.id}`
      const isNode = comp.component_type === 'semi_finished' || comp.component_type === 'product'
      const isExpanded = expandedKeys.has(childPath)
      const isDirectChild = depth === 1
      const unitAmountNum = Number(comp.unit_amount || comp.quantity || 0)
      const unitCostNum = Number(comp.birim_maliyet || 0)
      const recursiveUnitCost = getRecursiveUnitCost(
        comp.component_type,
        comp.component_id,
        unitCostNum,
        ancestorIds
      )
      const totalContribution = unitAmountNum * recursiveUnitCost

      // Look up sub-components if node
      let subComponents: ComponentItem[] = []
      let hasCycle = false
      if (isNode) {
        if (ancestorIds.has(comp.component_id)) {
          hasCycle = true
        } else if (comp.component_type === 'semi_finished') {
          subComponents = semiFinishedMap.get(comp.component_id)?.components || []
        } else if (comp.component_type === 'product') {
          subComponents = productsMap.get(comp.component_id)?.components || []
        }
      }

      return (
        <div key={childPath} className="bom-tree-item-wrapper">
          <div
            className={`bom-tree-row bom-tree-depth-${Math.min(depth, 3)}`}
            style={{ paddingLeft: `${(depth - 1) * 24 + 10}px` }}
          >
            <div className="bom-row-left">
              {isNode ? (
                <button
                  type="button"
                  className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                  aria-label={
                    isExpanded
                      ? `${comp.name} alt bileşenlerini daralt`
                      : `${comp.name} alt bileşenlerini genişlet`
                  }
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(childPath)}
                >
                  <ChevronIcon />
                </button>
              ) : (
                <span className="tree-leaf-spacer" aria-hidden="true" />
              )}
              {getTypeBadge(comp.component_type)}
              <code className="bom-code-badge">{comp.code}</code>
              <span className="bom-item-name" style={{ fontWeight: isDirectChild ? 600 : 400 }}>
                {comp.name}
              </span>
              {comp.component_type === 'semi_finished' && (() => {
                const sf = semiFinishedMap.get(comp.component_id)
                if (!sf || !isSemiFinishedEligibleForRecipeReport(sf)) return null
                return (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenReport?.(sf, 'semi_finished')
                    }}
                    title="Yarı Mamul Reçete Raporu"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 4px',
                      marginLeft: '6px',
                      lineHeight: 1,
                      verticalAlign: 'middle',
                    }}
                  >
                    <PieChartIcon size={12} style={{ marginRight: 0 }} />
                  </button>
                )
              })()}
              {!isDirectChild && (
                <span
                  className="badge badge-secondary"
                  style={{ fontSize: '0.68rem', padding: '1px 5px' }}
                  title="Bu bileşen üst yarı mamulün reçetesine aittir (Salt Okunur)"
                >
                  Salt Okunur
                </span>
              )}
            </div>

            <div className="bom-row-right">
              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Miktar:</span>
                <span className="tabular-nums bom-pill-value">
                  {formatQuantity(unitAmountNum)} {comp.unit}
                </span>
                {isDirectChild && (
                  <button
                    type="button"
                    className="cell-edit-btn"
                    aria-label={`${comp.name} birim miktarını düzenle`}
                    onClick={() => {
                      const parentN = Number(rootItem.recipe_output_quantity || 1)
                      const mVal = comp.recipe_amount != null ? Number(comp.recipe_amount) : unitAmountNum * parentN
                      setEditingCompQuantity({
                        parentItem: rootItem,
                        component: comp,
                        quantity: String(mVal),
                      })
                    }}
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Maliyet:</span>
                <span className="tabular-nums bom-pill-value">₺{unitCostNum.toFixed(2)}</span>
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Toplam Maliyet:</span>
                <span
                  className="tabular-nums bom-pill-value"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                >
                  ₺{totalContribution.toFixed(2)}
                </span>
              </div>

              <div className="bom-row-actions">
                {isDirectChild ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                    aria-label={`${comp.name} bileşenini kaldır`}
                    onClick={() => handleDeleteComponent(rootItem.id, comp.id)}
                  >
                    ✕ Kaldır
                  </button>
                ) : (
                  <span
                    className="text-muted"
                    style={{ fontSize: '0.75rem', width: '50px', textAlign: 'center', display: 'inline-block' }}
                    title="Alt yarı mamullerin bileşenleri kendi kartlarından yönetilir"
                  >
                    —
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Sub-components */}
          {isNode && isExpanded && (
            <div className="bom-sub-tree">
              {hasCycle ? (
                <div
                  className="bom-tree-alert"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  ⚠️ Döngüsel referans algılandı ({comp.name})
                </div>
              ) : subComponents.length === 0 ? (
                <div
                  className="bom-tree-empty-hint"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  Bu alt bileşenin tanımlı bir reçetesi bulunmuyor.
                </div>
              ) : (
                renderComponentTree(
                  rootItem,
                  subComponents,
                  depth + 1,
                  childPath,
                  new Set([...ancestorIds, comp.component_id])
                )
              )}
            </div>
          )}
        </div>
      )
    })
  }

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Yarı Mamuller</h2>
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx, .xls"
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            title="Excel dosyasından yarı mamulleri ve reçeteleri yükle"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Excel Yükle</span>
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleExportExcel}
            disabled={isProcessing}
            title="Yarı mamulleri ve reçete ağacını Excel olarak indir"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Excel İndir</span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Yeni Yarı Mamul
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Yarı mamul adı veya koduna göre ara…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="table-card">
        {filteredSemiFinished.length === 0 ? (
          <div className="empty-state">
            <p>{semiFinished.length === 0 ? 'Kayıtlı yarı mamul bulunmuyor.' : 'Arama kriterlerine uygun yarı mamul bulunamadı.'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: '130px' }}>Kod</th>
                <th style={{ minWidth: '200px' }}>Yarı Mamul Adı</th>
                <th style={{ minWidth: '110px' }}>Kategori</th>
                <th style={{ minWidth: '70px' }}>Birim</th>
                <th style={{ minWidth: '110px' }}>Maliyet</th>
                <th style={{ minWidth: '140px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredSemiFinished.map((item) => {
                const rootKey = `sf_${item.id}`
                const isExpanded = expandedKeys.has(rootKey)
                const comps = (item.components || []).filter((c) => !localHiddenComponentIds.has(c.id))

                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <div className="tree-cell-flex">
                          <button
                            type="button"
                            className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                            aria-label={
                              isExpanded
                                ? `${item.name} bileşenlerini daralt`
                                : `${item.name} bileşenlerini genişlet`
                            }
                            aria-expanded={isExpanded}
                            onClick={() => toggleExpand(rootKey)}
                          >
                            <ChevronIcon />
                          </button>
                          <strong>{item.code}</strong>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {item.image && (
                            <img
                              src={item.image}
                              alt=""
                              style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <span>{item.name}</span>
                        </div>
                      </td>
                      <td>{item.category?.name || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className="badge badge-secondary">{item.unit || 'Adet'}</span>
                      </td>
                      <td className="tabular-nums">
                        <strong>₺{calculateSemiFinishedTotalCost(item, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </td>
                      <td>
                        <div className="btn-group">
                          {isSemiFinishedEligibleForRecipeReport(item) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => onOpenReport?.(item, 'semi_finished')}
                              title="Reçete Raporu"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <PieChartIcon />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => openAddComponentModal(item)}
                            title="Bu yarı mamulün reçetesine yeni bileşen ekle"
                          >
                            + Bileşen
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation()
                              setGearMenu({ item, x: e.clientX, y: e.clientY })
                            }}
                            title="İşlemler (Düzenle / Demo Ortamına Kopyala)"
                          >
                            <SettingsGearIcon />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(item.id)}
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Cascading Tree Container */}
                    {isExpanded && (
                      <tr className="bom-expansion-row">
                        <td colSpan={7} className="bom-expansion-cell">
                          <div className="bom-dropdown-container">
                            {comps.length === 0 ? (
                              <div className="bom-empty-state">
                                <span>Bu yarı mamule henüz bir bileşen eklenmemiş.</span>
                                <button
                                  type="button"
                                  className="tree-add-btn"
                                  onClick={() => openAddComponentModal(item)}
                                >
                                  + İlk Bileşeni Ekle
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="bom-tree-list">
                                  {renderComponentTree(item, comps, 1, rootKey, new Set([item.id]))}
                                </div>
                                <div className="bom-tree-add-footer">
                                  <button
                                    type="button"
                                    className="tree-add-btn"
                                    onClick={() => openAddComponentModal(item)}
                                  >
                                    + <strong>{item.name}</strong> İçin Yeni Bileşen Ekle
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Yeni Yarı Mamul Ekle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-semi-code">Yarı Mamul Kodu *</label>
                    <input
                      id="add-semi-code"
                      name="code"
                      required
                      autoComplete="off"
                      spellCheck={false}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Örn: YM-001…"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-semi-name">Yarı Mamul Adı *</label>
                    <input
                      id="add-semi-name"
                      name="name"
                      required
                      autoComplete="off"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Örn: Hoparlör Sürücü Modülü…"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-semi-category">Kategori</label>
                    <select
                      id="add-semi-category"
                      name="category_id"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                    >
                      <option value="">Kategori Seçin…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-semi-unit">Ölçü Birimi</label>
                    <select
                      id="add-semi-unit"
                      name="unit"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    >
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="add-semi-recipe-qty">Reçete Sonucu Oluşan Yarı Mamul Miktarı</label>
                    <input
                      id="add-semi-recipe-qty"
                      name="recipe_output_quantity"
                      type="number"
                      step="any"
                      min="0.0001"
                      required
                      autoComplete="off"
                      value={recipeOutputQuantity}
                      onChange={(e) => setRecipeOutputQuantity(e.target.value)}
                      placeholder="1"
                    />
                    <span className="helper-text">
                      Reçetedeki bileşen miktarlarının kaç {unit || 'Adet'} yarı mamul ürettiği
                    </span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-semi-image">Görsel URL</label>
                    <input
                      id="add-semi-image"
                      name="image"
                      autoComplete="off"
                      spellCheck={false}
                      value={image}
                      onChange={(e) => setImage(e.target.value)}
                      placeholder="https://…"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-semi-barcode">Barkod</label>
                    <input
                      id="add-semi-barcode"
                      name="barcode"
                      autoComplete="off"
                      spellCheck={false}
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      placeholder="8690…"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="add-semi-description">Açıklama</label>
                  <input
                    id="add-semi-description"
                    name="description"
                    autoComplete="off"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Teknik detaylar…"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Yarı Mamulü Düzenle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setEditingItem(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-semi-code">Yarı Mamul Kodu *</label>
                    <input
                      id="edit-semi-code"
                      name="code"
                      required
                      autoComplete="off"
                      spellCheck={false}
                      value={editCode}
                      onChange={(e) => setEditCode(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-semi-name">Yarı Mamul Adı *</label>
                    <input
                      id="edit-semi-name"
                      name="name"
                      required
                      autoComplete="off"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-semi-category">Kategori</label>
                    <select
                      id="edit-semi-category"
                      name="category_id"
                      value={editCategoryId}
                      onChange={(e) => setEditCategoryId(e.target.value)}
                    >
                      <option value="">Kategori Seçin…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-semi-unit">Ölçü Birimi</label>
                    <select
                      id="edit-semi-unit"
                      name="unit"
                      value={editUnit}
                      onChange={(e) => setEditUnit(e.target.value)}
                    >
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="edit-semi-recipe-qty">Reçete Sonucu Oluşan Yarı Mamul Miktarı</label>
                    <input
                      id="edit-semi-recipe-qty"
                      name="recipe_output_quantity"
                      type="number"
                      step="any"
                      min="0.0001"
                      required
                      autoComplete="off"
                      value={editRecipeOutputQuantity}
                      onChange={(e) => setEditRecipeOutputQuantity(e.target.value)}
                    />
                    <span className="helper-text">
                      Reçetedeki bileşen miktarlarının kaç {editUnit || 'Adet'} yarı mamul ürettiği
                    </span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-semi-image">Görsel URL</label>
                    <input
                      id="edit-semi-image"
                      name="image"
                      autoComplete="off"
                      spellCheck={false}
                      value={editImage}
                      onChange={(e) => setEditImage(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-semi-barcode">Barkod</label>
                    <input
                      id="edit-semi-barcode"
                      name="barcode"
                      autoComplete="off"
                      spellCheck={false}
                      value={editBarcode}
                      onChange={(e) => setEditBarcode(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-semi-description">Açıklama</label>
                  <input
                    id="edit-semi-description"
                    name="description"
                    autoComplete="off"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingItem(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Component to Root Semi-Finished Modal */}
      {activeItemForAddComponent && (
        <div className="modal-backdrop">
          <div className="modal-box modal-lg">
            <div className="modal-header">
              <div>
                <h3>Yarı Mamul Reçetesine Yeni Bileşen Ekle</h3>
                <p className="subtitle" style={{ margin: 0 }}>
                  <strong>{activeItemForAddComponent.name}</strong> ({activeItemForAddComponent.code}) için girdi bileşeni
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setActiveItemForAddComponent(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddComponent}>
              <div className="modal-body">
                {compError && (
                  <div className="alert alert-danger" aria-live="polite">
                    {compError}
                  </div>
                )}

                <div className="form-row">
                  {/* Step 1: Component Category / Type */}
                  <div className="form-group">
                    <label htmlFor="semi-comp-type">1. Bileşen Türü *</label>
                    <select
                      id="semi-comp-type"
                      name="component_type"
                      value={selectedCompType}
                      onChange={(e) => {
                        setSelectedCompType(e.target.value as ComponentType)
                        setSelectedItemId('')
                      }}
                    >
                      <option value="raw_material">Hammadde</option>
                      <option value="packaging">Ambalaj</option>
                      <option value="semi_finished">Yarı Mamul</option>
                      <option value="product">Mamul Ürün</option>
                      <option value="trading_good">Al-Sat Ürün</option>
                    </select>
                  </div>

                  {/* Step 2: Item Selection from Filtered Category */}
                  <div className="form-group" style={{ flex: 2 }}>
                    <label htmlFor="semi-comp-item">2. Kayıt Seçin *</label>
                    <SearchableComponentSelect
                      id="semi-comp-item"
                      options={getSelectableItems()}
                      value={selectedItemId}
                      onChange={(id) => {
                        setSelectedItemId(id)
                        setCompUnitMode('kg')
                      }}
                      placeholder="Bileşen adı veya kodu ile arayın…"
                      required
                    />
                  </div>
                </div>

                {(() => {
                  const selectedUnit = getSelectedComponentUnit()
                  const isKg = selectedUnit.trim().toLowerCase() === 'kg'
                  return (
                    <div className="form-row" style={{ marginTop: '1rem', flexDirection: 'column' }}>
                      {isKg && (
                        <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Giriş Birimi:</span>
                          <div className="btn-group" role="group" style={{ display: 'inline-flex' }}>
                            <button
                              type="button"
                              className={`btn btn-sm ${compUnitMode === 'kg' ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={() => setCompUnitMode('kg')}
                            >
                              Kg
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${compUnitMode === 'g' ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={() => setCompUnitMode('g')}
                            >
                              g (Gram)
                            </button>
                          </div>
                          {compUnitMode === 'g' && (
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              (Kaydedilirken Kg'ye çevrilecektir: {parseFloat(compQuantity.replace(',', '.')) ? formatQuantity(parseFloat(compQuantity.replace(',', '.')) / 1000) : '0'} Kg)
                            </span>
                          )}
                        </div>
                      )}

                      {/* Step 3: Quantity / Amount per n */}
                      <div className="form-group" style={{ width: '100%' }}>
                        <label htmlFor="semi-comp-amount">
                          3. {Number(activeItemForAddComponent.recipe_output_quantity || 1)} {activeItemForAddComponent.unit} Başına Miktar ({isKg && compUnitMode === 'g' ? 'Gram' : selectedUnit || 'Birim'}) *
                        </label>
                        <input
                          id="semi-comp-amount"
                          name="unit_amount"
                          type="number"
                          step="any"
                          min="0.0001"
                          required
                          autoComplete="off"
                          value={compQuantity}
                          onChange={(e) => setCompQuantity(e.target.value)}
                          placeholder={isKg && compUnitMode === 'g' ? '0.6…' : '1.0000…'}
                        />
                        <span className="helper-text">
                          {Number(activeItemForAddComponent.recipe_output_quantity || 1)} {activeItemForAddComponent.unit} {activeItemForAddComponent.name} üretmek için gereken miktar
                        </span>
                      </div>
                    </div>
                  )
                })()}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveItemForAddComponent(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Bileşeni Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Component Quantity Modal */}
      {editingCompQuantity && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3>Bileşen Miktarını Düzenle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setEditingCompQuantity(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveComponentQuantity}>
              <div className="modal-body">
                <p className="subtitle">
                  <strong>{editingCompQuantity.parentItem.name}</strong> için girdi olan{' '}
                  <strong>{editingCompQuantity.component.name}</strong> ({editingCompQuantity.component.code})
                </p>
                <div className="form-group">
                  <label htmlFor="edit-semi-comp-quantity">
                    {Number(editingCompQuantity.parentItem.recipe_output_quantity || 1)} {editingCompQuantity.parentItem.unit} Başına Miktar ({editingCompQuantity.component.unit})
                  </label>
                  <input
                    id="edit-semi-comp-quantity"
                    name="quantity"
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    required
                    autoComplete="off"
                    value={editingCompQuantity.quantity}
                    onChange={(e) =>
                      setEditingCompQuantity({
                        ...editingCompQuantity,
                        quantity: e.target.value,
                      })
                    }
                  />
                  <span className="helper-text" style={{ fontSize: '0.8rem', marginTop: '0.25rem', display: 'block', color: 'var(--color-text-muted)' }}>
                    Birim miktar (m/n): {(parseFloat(editingCompQuantity.quantity) ? formatQuantity(parseFloat(editingCompQuantity.quantity) / Number(editingCompQuantity.parentItem.recipe_output_quantity || 1)) : '0')} {editingCompQuantity.component.unit}
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingCompQuantity(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gear Context Menu */}
      {gearMenu && (
        <div
          className="gear-context-menu-backdrop"
          style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
          onClick={() => setGearMenu(null)}
        >
          <div
            className="gear-dropdown-menu"
            style={{
              position: 'fixed',
              left: Math.min(gearMenu.x, window.innerWidth - 200),
              top: Math.min(gearMenu.y, window.innerHeight - 100),
              backgroundColor: '#fff',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #e5e7eb',
              padding: '4px 0',
              minWidth: '180px',
              zIndex: 1001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gear-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#374151',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => {
                const item = gearMenu.item
                setGearMenu(null)
                openEditModal(item)
              }}
            >
              <PencilIcon size={13} />
              <span>Düzenle</span>
            </button>
            <button
              type="button"
              className="gear-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#374151',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={async () => {
                const item = gearMenu.item
                setGearMenu(null)
                try {
                  const demoRes = await api.copySemiFinishedToDemo(item.id)
                  alert(`"${item.name}" başarıyla demo ortama kopyalandı. "Demo Yarı Mamuller" sekmesinden inceleyebilirsiniz.`)
                  if (setDemoSemiFinished) {
                    setDemoSemiFinished((prev) => [demoRes, ...prev])
                  } else {
                    await onRefresh()
                  }
                } catch (err: any) {
                  alert('Kopyalama sırasında hata oluştu: ' + (err.message || 'Bilinmeyen hata'))
                }
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <span>Demo Ortamına Kopyala</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
